import React, {createContext, useContext} from 'react';

export type ProjectPageContextType = {
  projectId: string;
  projectName: string;
  projectEntityName: string;
};

export const ProjectPageContext = createContext<
  ProjectPageContextType | undefined
>(undefined);

type ProjectPageContextProviderProps = ProjectPageContextType & {
  children: React.ReactNode;
};

/**
 * This context holds values that are stable for the lifetime of a project
 * page visit — project identity and product-mode flags.
 *
 * NOTE: ProjectPage, SweepsPage, and GroupPage all use this context.
 *
 * When any value changes, every consumer re-renders, so keep the following
 * guidelines in mind:
 *
 *   DO add: primitives / booleans / strings that change rarely (access flags,
 *           project IDs, feature gates, run count).
 *
 *   DO NOT add: large objects (e.g. the full `project` GraphQL result),
 *               frequently changing data (e.g. polling results, selection
 *               state), or anything that only a single route needs — pass
 *               those as props or use a more scoped context instead.
 *
 * Access-related flags (hasModelsAccess, hasWeaveAccess, etc.) live in
 * ProjectAccessContext, which is only provided within the ProjectPage tree.
 */
export const ProjectPageContextProvider = ({
  children,
  projectId,
  projectName,
  projectEntityName,
}: ProjectPageContextProviderProps) => {
  const value = {
    projectId,
    projectName,
    projectEntityName,
  };
  // React Compiler caches the context value; this component must stay
  // compiled for that to hold.
  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values
    <ProjectPageContext.Provider value={value}>
      {children}
    </ProjectPageContext.Provider>
  );
};

export const useProjectPageContext = () => {
  const context = useContext(ProjectPageContext);
  if (!context) {
    throw new Error(
      'useProjectPageContext must be used within a ProjectPageContextProvider'
    );
  }
  return context;
};
